// src/components/BuffettDCFInputs.js
import React, { useState, useEffect, useMemo } from 'react';

export default function BuffettDCFInputs({
    financials = {},
    fcfHistory = [],
    onChange,
    sharesOutstanding
}) {
    const parseSeries = (obj = {}, key) => {
        const map = obj[key] || {};
        return Object.keys(map)
            .sort()
            .map(yr => {
                const v = String(map[yr]).replace(/,/g, '');
                return isNaN(+v) ? 0 : +v;
            });
    };

    const rollingAvg = (arr, n) =>
        arr.length >= n ? arr.slice(-n).reduce((s, x) => s + x, 0) / n : 0;

    // ——————————————————————————————
    // 1) Depreciation proxy
    // ——————————————————————————————
    const pl = financials.profitAndLoss || {};
    const deprKey = pl['Depreciation & Amortization']
        ? 'Depreciation & Amortization'
        : 'Depreciation';
    const deprSeries = parseSeries(pl, deprKey);
    const deprOptions = useMemo(() => ({
        current: deprSeries.at(-1) || 0,
        '3yr': rollingAvg(deprSeries, 3),
        '5yr': rollingAvg(deprSeries, 5),
        custom: 0
    }), [deprSeries]);

    // ——————————————————————————————
    // 2) CapEx/Revenue ratio proxy
    // ——————————————————————————————
    const revSeries = parseSeries(pl, 'Sales');
    const cf = financials.cashFlow || {};
    const cfoSeries = parseSeries(cf, 'Cash from Operating Activity');
    const cfo = cfoSeries.at(-1) || 0;

    const purchased = parseSeries(cf, 'Fixed assets purchased').map(x => Math.abs(x));
    const sold = parseSeries(cf, 'Fixed assets sold').map(x => Math.abs(x));
    const capexSeriesRaw = purchased.map((p, i) => Math.max(0, p - (sold[i] || 0)));
    const ratioSeries = revSeries.map((r, i) => (r > 0 ? capexSeriesRaw[i] / r : 0));
    const currentRev = revSeries.at(-1) || 0;
    const ratioOptions = useMemo(() => ({
        current: ratioSeries.at(-1) * currentRev,
        '3yr': rollingAvg(ratioSeries, 3) * currentRev,
        '5yr': rollingAvg(ratioSeries, 5) * currentRev,
        custom: 0
    }), [ratioSeries, currentRev]);

    // ——————————————————————————————
    // 3) Balance‐sheet split proxy
    // ——————————————————————————————
    const bs = financials.balanceSheet || {};
    const gppeSeries = parseSeries(bs, 'Gross Block');
    const growthSeries = gppeSeries.map((v, i) => (i > 0 ? Math.max(0, v - gppeSeries[i - 1]) : 0));
    const maintSeries = capexSeriesRaw.map((cap, i) => cap - (growthSeries[i] || 0));
    const bsOptions = useMemo(() => ({
        current: maintSeries.at(-1) || 0,
        '3yr': rollingAvg(maintSeries, 3),
        '5yr': rollingAvg(maintSeries, 5),
        custom: 0
    }), [maintSeries]);


    // ——————————————————————————————
    // Method & mode state
    // ——————————————————————————————
    const [method, setMethod] = useState('depreciation');
    const [modes, setModes] = useState({
        depreciation: 'current',
        ratio: 'current',
        bsplit: 'current'
    });
    const [customVals, setCustomVals] = useState({
        depreciation: '',
        ratio: '',
        bsplit: ''
    });
    const optsMap = { depreciation: deprOptions, ratio: ratioOptions, bsplit: bsOptions };

    const maintenanceCapEx = (() => {
        const mode = modes[method];
        if (mode === 'custom') {
            return parseFloat(customVals[method]) || 0;
        }
        return optsMap[method][mode] || 0;
    })();

    // ——————————————————————————————
    // Multi‐stage growth & terminal growth
    // ——————————————————————————————
    const computeCAGR = (start, end, yrs) =>
        yrs > 0 && start > 0
            ? Math.pow(end / start, 1 / yrs) - 1
            : 0;

    const n = fcfHistory.length;
    const cagrOptions = useMemo(() => ({
        '3yr': computeCAGR(fcfHistory[n - 4], fcfHistory[n - 1], 3),
        '5yr': computeCAGR(fcfHistory[n - 6], fcfHistory[n - 1], 5),
        '7yr': computeCAGR(fcfHistory[n - 8], fcfHistory[n - 1], 7),
        '10yr': computeCAGR(fcfHistory[n - 11], fcfHistory[n - 1], 10),
        custom: 0
    }), [fcfHistory, n]);

    // Stage durations
    const durationChoices = ['3', '5', '7', '10', 'custom'];
    const [s1DurMode, setS1DurMode] = useState('5');
    const [s1DurCustom, setS1DurCustom] = useState('');
    const stage1Years = s1DurMode === 'custom'
        ? (parseInt(s1DurCustom, 10) || 0)
        : parseInt(s1DurMode, 10);

    const [s2DurMode, setS2DurMode] = useState('3');
    const [s2DurCustom, setS2DurCustom] = useState('');
    const stage2Years = s2DurMode === 'custom'
        ? (parseInt(s2DurCustom, 10) || 0)
        : parseInt(s2DurMode, 10);

    // Stage growth
    const growthChoices = Object.keys(cagrOptions);
    const [s1GrMode, setS1GrMode] = useState('5yr');
    const [s1GrCustom, setS1GrCustom] = useState(0.15);
    const stage1Growth = s1GrMode === 'custom'
        ? s1GrCustom
        : cagrOptions[s1GrMode];

    const [s2GrMode, setS2GrMode] = useState('3yr');
    const [s2GrCustom, setS2GrCustom] = useState(0.06);
    const stage2Growth = s2GrMode === 'custom'
        ? s2GrCustom
        : cagrOptions[s2GrMode];

    // Terminal growth
    const termChoices = ['2%', '3%', '4%', 'custom'];
    const [termMode, setTermMode] = useState('2%');
    const [termCustom, setTermCustom] = useState(0.025);
    const terminalGrowth = termMode === 'custom'
        ? termCustom
        : parseFloat(termMode) / 100;

    // ——————————————————————————————
    // Discount & Moat
    // ——————————————————————————————
    const [discountRate, setDiscountRate] = useState(0.10);
    const MOAT = [
        { label: 'Wide', adj: -0.01 },
        { label: 'Narrow', adj: 0.00 },
        { label: 'None', adj: 0.01 }
    ];
    const [moat, setMoat] = useState('Narrow');
    const moatAdj = MOAT.find(m => m.label === moat)?.adj || 0;
    const adjDiscountRate = discountRate + moatAdj;

    // ——————————————————————————————
    // Net Debt & Shares Outstanding
    // ——————————————————————————————
    const debtKeys = [
        'Borrowings'
    ];
    const grossDebt = debtKeys.reduce((sum, key) => {
        const series = parseSeries(bs, key);
        return sum + (series.at(-1) || 0);
    }, 0);
    const cash = parseSeries(bs, 'Cash Equivalents').at(-1) || 0;
    const defaultDebt = grossDebt - cash;
    const [netDebt, setNetDebt] = useState(defaultDebt);
    const ownerEarnings = cfo - maintenanceCapEx;

    // ——————————————————————————————
    // Push all inputs upstream
    // ——————————————————————————————
    useEffect(() => {
        onChange({
            ownerEarnings,
            maintenanceCapEx,
            stage1Years,
            stage1Growth,
            stage2Years,
            stage2Growth,
            terminalGrowth,
            discountRate: adjDiscountRate,
            moat,
            netDebt,
            sharesOutstanding
        });
    }, [
        ownerEarnings,
        maintenanceCapEx,
        stage1Years, stage1Growth,
        stage2Years, stage2Growth,
        terminalGrowth,
        adjDiscountRate, moat,
        netDebt, sharesOutstanding,
        onChange
    ]);

    // ——————————————————————————————
    // Format helpers
    // ——————————————————————————————

    const fmtCr = v => `₹ ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })} Cr`;
    const fmtPct = v => `${(v * 100).toFixed(1)}%`;

    // ——————————————————————————————
    // Render
    // ——————————————————————————————
    return (
        <div className="space-y-5">
            {/* Maintenance CapEx Method */}
            <div>
                <label className="block font-medium mb-1">Maintenance CapEx Method</label>
                <select
                    className="border rounded px-2 py-1"
                    value={method}
                    onChange={e => setMethod(e.target.value)}
                >
                    <option value="depreciation">Depreciation Proxy</option>
                    <option value="ratio">CapEx/Revenue Ratio</option>
                    <option value="bsplit">Balance‐Sheet Split</option>
                </select>
            </div>

            <div>
                <label className="block font-medium mb-1">Maintenance CapEx</label>
                <select
                    className="border rounded px-2 py-1 w-full"
                    value={modes[method]}
                    onChange={e => setModes(m => ({ ...m, [method]: e.target.value }))}
                >
                    <option value="current">Current ({fmtCr(optsMap[method].current)})</option>
                    <option value="3yr">3-Yr Avg ({fmtCr(optsMap[method]['3yr'])})</option>
                    <option value="5yr">5-Yr Avg ({fmtCr(optsMap[method]['5yr'])})</option>
                    <option value="custom">Custom</option>
                </select>
                {modes[method] === 'custom' && (
                    <input
                        type="number"
                        className="border rounded px-2 py-1 mt-2 w-full"
                        placeholder="Enter custom Maintenance CapEx"
                        value={customVals[method]}
                        onChange={e => setCustomVals(v => ({ ...v, [method]: e.target.value }))}
                    />
                )}
            </div>

            {/* Stage 1 */}
            <div>
                <label className="block font-medium mb-1">Stage 1 Duration (yrs)</label>
                <select
                    className="border rounded px-2 py-1 w-32"
                    value={s1DurMode}
                    onChange={e => setS1DurMode(e.target.value)}
                >
                    {durationChoices.map(opt => (
                        <option key={opt} value={opt}>
                            {opt === 'custom' ? 'Custom' : `${opt} yrs`}
                        </option>
                    ))}
                </select>
                {s1DurMode === 'custom' && (
                    <input
                        type="number"
                        className="border rounded px-2 py-1 ml-2 w-24"
                        value={s1DurCustom}
                        onChange={e => setS1DurCustom(e.target.value)}
                    />
                )}
            </div>
            <div>
                <label className="block font-medium mb-1">Stage 1 Growth Rate</label>
                <select
                    className="border rounded px-2 py-1 w-48"
                    value={s1GrMode}
                    onChange={e => setS1GrMode(e.target.value)}
                >
                    {growthChoices.map(key => (
                        <option key={key} value={key}>
                            {key === 'custom'
                                ? 'Custom'
                                : `${key.replace('yr', '-Yr')} CAGR (${fmtPct(cagrOptions[key])})`
                            }
                        </option>
                    ))}
                </select>
                {s1GrMode === 'custom' && (
                    <>
                        <input
                            type="range"
                            min={0} max={0.3} step={0.001}
                            className="w-full mt-2"
                            value={s1GrCustom}
                            onChange={e => setS1GrCustom(Number(e.target.value))}
                        />
                        <div className="text-right text-sm mt-1">{fmtPct(s1GrCustom)}</div>
                    </>
                )}
            </div>

            {/* Stage 2 */}
            <div>
                <label className="block font-medium mb-1">Stage 2 Duration (yrs)</label>
                <select
                    className="border rounded px-2 py-1 w-32"
                    value={s2DurMode}
                    onChange={e => setS2DurMode(e.target.value)}
                >
                    {durationChoices.map(opt => (
                        <option key={opt} value={opt}>
                            {opt === 'custom' ? 'Custom' : `${opt} yrs`}
                        </option>
                    ))}
                </select>
                {s2DurMode === 'custom' && (
                    <input
                        type="number"
                        className="border rounded px-2 py-1 ml-2 w-24"
                        value={s2DurCustom}
                        onChange={e => setS2DurCustom(e.target.value)}
                    />
                )}
            </div>
            <div>
                <label className="block font-medium mb-1">Stage 2 Growth Rate</label>
                <select
                    className="border rounded px-2 py-1 w-48"
                    value={s2GrMode}
                    onChange={e => setS2GrMode(e.target.value)}
                >
                    {growthChoices.map(key => (
                        <option key={key} value={key}>
                            {key === 'custom'
                                ? 'Custom'
                                : `${key.replace('yr', '-Yr')} CAGR (${fmtPct(cagrOptions[key])})`
                            }
                        </option>
                    ))}
                </select>
                {s2GrMode === 'custom' && (
                    <>
                        <input
                            type="range"
                            min={0} max={0.2} step={0.001}
                            className="w-full mt-2"
                            value={s2GrCustom}
                            onChange={e => setS2GrCustom(Number(e.target.value))}
                        />
                        <div className="text-right text-sm mt-1">{fmtPct(s2GrCustom)}</div>
                    </>
                )}
            </div>

            {/* Terminal Growth */}
            <div>
                <label className="block font-medium mb-1">Terminal Growth Rate</label>
                <select
                    className="border rounded px-2 py-1 w-32"
                    value={termMode}
                    onChange={e => setTermMode(e.target.value)}
                >
                    {termChoices.map(opt => (
                        <option key={opt} value={opt}>
                            {opt === 'custom' ? 'Custom' : opt}
                        </option>
                    ))}
                </select>
                {termMode === 'custom' && (
                    <>
                        <input
                            type="range"
                            min={0} max={0.05} step={0.001}
                            className="w-full mt-2"
                            value={termCustom}
                            onChange={e => setTermCustom(Number(e.target.value))}
                        />
                        <div className="text-right text-sm mt-1">{fmtPct(termCustom)}</div>
                    </>
                )}
            </div>

            {/* Discount & Moat */}
            <div>
                <label className="block font-medium mb-1">Discount Rate</label>
                <input
                    type="range"
                    min={0} max={0.2} step={0.001}
                    className="w-full"
                    value={discountRate}
                    onChange={e => setDiscountRate(Number(e.target.value))}
                />
                <div className="flex justify-between text-sm mt-1">
                    <span>{fmtPct(discountRate)}</span>
                    <span>
                        Moat:{' '}
                        <select
                            className="border rounded px-1 py-0.5"
                            value={moat}
                            onChange={e => setMoat(e.target.value)}
                        >
                            {MOAT.map(m => (
                                <option key={m.label} value={m.label}>{m.label}</option>
                            ))}
                        </select>
                    </span>
                </div>
            </div>

            {/* Net Debt & Shares */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block font-medium mb-1">Net Debt</label>
                    <input
                        type="number"
                        className="border rounded px-2 py-1 w-full"
                        value={netDebt}
                        onChange={e => setNetDebt(Number(e.target.value))}
                    />
                </div>
                <div>
                    <label className="block font-medium mb-1">Shares Outstanding</label>
                    <input
                        type="number"
                        className="border rounded px-2 py-1 w-full"
                        value={sharesOutstanding}
                        readOnly
                    />
                </div>
            </div>
        </div>
    );
}
